import { stairPlacement } from '@stair-craft/calc/util';
import { IRailingGeometry, IRoomGeometry, IStairCalculationResult, IStairConfig, IStepGeometry, IVec3 } from '@stair-craft/common/util';

import { sectorBulges } from './arc-bulge';
import { horizontalDim, verticalDim } from './dim-builders';
import {
    EColorRef,
    ELineCap,
    ELineJoin,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    FONT_MONO_FAMILY,
    IFont,
    IPaneRect,
    IPlanPrimitive,
    IPoint,
    IPolyPrimitive,
    IProjectionOutput,
    ITextPrimitive,
    IViewFit,
} from './plan-primitive';
import { ISpiralPoint, spiralNumberAt, spiralPole, spiralWalkArc } from './spiral-plan-marks';
import { ITextMeasurer } from './text-measurer';
import {
    IStairPoint,
    inStairFrame,
    projectFlightLengths,
    projectParameterNotes,
    projectTreadMarks,
    projectWalkingLine,
    walkingLine,
} from './top-plan-marks';

const PLAN_FIXED_PX_PER_MM: number = 0.14;
const PAD_LEFT: number = 28;
const PAD_RIGHT: number = 64;
const PAD_TOP: number = 36;
const PAD_BOTTOM: number = 56;

/** The pixel margins the top view keeps around its focus box for the dimension rows. */
export const TOP_PLAN_PADS: { left: number; right: number; top: number; bottom: number } = {
    left: PAD_LEFT,
    right: PAD_RIGHT,
    top: PAD_TOP,
    bottom: PAD_BOTTOM,
};
const FOCUS_PADDING_MM: number = 250;
const WALL_THICKNESS_MM: number = 120;
const HATCH_STEP: number = 12;
const OPENING_FILL_ALPHA: number = 0.14;
const OPENING_HATCH_ALPHA: number = 0.32;
const EXT_LINE_ALPHA: number = 0.45;
const EXT_LINE_WIDTH: number = 0.5;
const EXT_LINE_DASH: readonly number[] = [3, 3];
const OPENING_DASH: readonly number[] = [8, 4];
/** How far past a round opening's circle its label stands, along each axis, pixels. */
const OPENING_LABEL_GAP: number = 8;
const DIRECTION_HEAD_LEN: number = 10;
const AXIS_INSET: number = 20;
const AXIS_ARROW_LEN: number = 50;
const AXIS_HEAD_LEN: number = 10;

const OPENING_LABEL_FONT: IFont = { weight: 500, size: 11, family: FONT_MONO_FAMILY };
const ARROW_LABEL_FONT: IFont = { weight: 400, size: 9, family: FONT_MONO_FAMILY };
const AXIS_LABEL_FONT: IFont = { weight: 500, size: 12, family: FONT_MONO_FAMILY };
const AXIS_ORIGIN_FONT: IFont = { weight: 500, size: 11, family: FONT_MONO_FAMILY };

/** A plan box in room millimetres: corner (x, z), width along X, length along Z. */
export interface IPlanBox {
    x: number;
    z: number;
    w: number;
    l: number;
}

export interface IPlanOffset {
    x: number;
    z: number;
}

/** Pane-local world-to-pixel transform; the drawer translates to the pane origin. */
interface IProjector {
    pwx(x: number): number;
    pwy(z: number): number;
    pws(s: number): number;
}

function rectPoints(x: number, y: number, w: number, h: number): IPoint[] {
    return [
        [x, y],
        [x + w, y],
        [x + w, y + h],
        [x, y + h],
    ];
}

function fillRect(x: number, y: number, w: number, h: number, fill: EColorRef, fillAlpha?: number): IPolyPrimitive {
    return { kind: EPrimitiveKind.Poly, points: rectPoints(x, y, w, h), closed: true, fill, fillAlpha };
}

function strokeRect(
    x: number,
    y: number,
    w: number,
    h: number,
    stroke: EColorRef,
    width: number,
    dash?: readonly number[]
): IPolyPrimitive {
    return { kind: EPrimitiveKind.Poly, points: rectPoints(x, y, w, h), closed: true, stroke, width, dash };
}

function line(
    from: IPoint,
    to: IPoint,
    stroke: EColorRef,
    width: number,
    options?: { dash?: readonly number[]; strokeAlpha?: number }
): IPolyPrimitive {
    return {
        kind: EPrimitiveKind.Poly,
        points: [from, to],
        closed: false,
        dash: options?.dash,
        strokeAlpha: options?.strokeAlpha,
        stroke,
        width,
    };
}

function text(at: IPoint, value: string, align: ETextAlign, baseline: ETextBaseline, color: EColorRef, font: IFont): ITextPrimitive {
    return { kind: EPrimitiveKind.Text, text: value, at, align, baseline, color, font };
}

/** Where the stair's own (0, 0) sits in room millimetres — the placement the viewport and the calculators use. */
export function stairOffset(result: IStairCalculationResult, config: IStairConfig): IPlanOffset {
    return stairPlacement(config, result.room, result.totalRun);
}

function stairBBox(result: IStairCalculationResult, config: IStairConfig): IPlanBox | null {
    const steps: IStepGeometry[] = result.geometry.steps;
    if (steps.length === 0) {
        return null;
    }
    const offset: IPlanOffset = stairOffset(result, config);
    let minX: number = Number.POSITIVE_INFINITY;
    let minZ: number = Number.POSITIVE_INFINITY;
    let maxX: number = Number.NEGATIVE_INFINITY;
    let maxZ: number = Number.NEGATIVE_INFINITY;

    for (const step of steps) {
        if (step.winderCorners) {
            const corners: NonNullable<IStepGeometry['winderCorners']> = step.winderCorners;
            const xs: number[] = [corners.innerBack.x, corners.innerFront.x, corners.outerBack.x, corners.outerFront.x];
            const zs: number[] = [corners.innerBack.z, corners.innerFront.z, corners.outerBack.z, corners.outerFront.z];
            minX = Math.min(minX, ...xs);
            maxX = Math.max(maxX, ...xs);
            minZ = Math.min(minZ, ...zs);
            maxZ = Math.max(maxZ, ...zs);
        } else {
            minX = Math.min(minX, step.position.x);
            maxX = Math.max(maxX, step.position.x + step.treadWidth);
            minZ = Math.min(minZ, step.position.z);
            maxZ = Math.max(maxZ, step.position.z + step.treadDepth);
        }
    }
    for (const landing of result.geometry.landings) {
        minX = Math.min(minX, landing.position.x);
        maxX = Math.max(maxX, landing.position.x + landing.width);
        minZ = Math.min(minZ, landing.position.z);
        maxZ = Math.max(maxZ, landing.position.z + landing.depth);
    }
    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
        return null;
    }
    return { x: offset.x + minX, z: offset.z + minZ, w: maxX - minX, l: maxZ - minZ };
}

/** The part of the room the top view shows: the stair and the opening with a margin, room millimetres. */
export function focusBBox(result: IStairCalculationResult, config: IStairConfig): IPlanBox {
    const room: IRoomGeometry = result.room;
    const oX: number = room.openingX;
    const oZ: number = room.openingZ;
    const oW: number = room.openingWidth;
    const oL: number = room.openingLength;
    const sBox: IPlanBox | null = stairBBox(result, config);

    const minX: number = sBox ? Math.min(sBox.x, oX) : oX;
    const maxX: number = sBox ? Math.max(sBox.x + sBox.w, oX + oW) : oX + oW;
    const minZ: number = sBox ? Math.min(sBox.z, oZ) : oZ;
    const maxZ: number = sBox ? Math.max(sBox.z + sBox.l, oZ + oL) : oZ + oL;

    return {
        x: minX - FOCUS_PADDING_MM,
        z: minZ - FOCUS_PADDING_MM,
        w: maxX - minX + 2 * FOCUS_PADDING_MM,
        l: maxZ - minZ + 2 * FOCUS_PADDING_MM,
    };
}

/** A pixel rectangle cut to a clip rectangle; null when nothing of it is left. */
function clipRect(x: number, y: number, w: number, h: number, clip: IPlanBox): IPlanBox | null {
    const left: number = Math.max(x, clip.x);
    const top: number = Math.max(y, clip.z);
    const right: number = Math.min(x + w, clip.x + clip.w);
    const bottom: number = Math.min(y + h, clip.z + clip.l);
    return right > left && bottom > top ? { x: left, z: top, w: right - left, l: bottom - top } : null;
}

/**
 * The room walls and outline, cut to the part of the room the view shows: the sheet shows only
 * that part, and a wall running on past it would stand under the notes over the view.
 */
function projectRoom(room: IRoomGeometry, focus: IPlanBox, projector: IProjector, out: IPlanPrimitive[]): void {
    const wallThickness: number = Math.max(4, projector.pws(WALL_THICKNESS_MM));
    const x0: number = projector.pwx(0);
    const y0: number = projector.pwy(0);
    const rw: number = projector.pws(room.roomWidth);
    const rl: number = projector.pws(room.roomLength);
    const wt: number = wallThickness;
    const view: IPlanBox = {
        x: projector.pwx(focus.x) - wt,
        z: projector.pwy(focus.z) - wt,
        w: projector.pws(focus.w) + 2 * wt,
        l: projector.pws(focus.l) + 2 * wt,
    };
    const walls: [number, number, number, number][] = [
        [x0 - wt, y0 - wt, rw + wt * 2, wt],
        [x0 - wt, y0 + rl, rw + wt * 2, wt],
        [x0 - wt, y0, wt, rl],
        [x0 + rw, y0, wt, rl],
    ];
    for (const [x, y, w, h] of walls) {
        const cut: IPlanBox | null = clipRect(x, y, w, h, view);
        if (cut) {
            out.push(fillRect(cut.x, cut.z, cut.w, cut.l, EColorRef.Ink5));
        }
    }
    const outline: IPlanBox | null = clipRect(x0, y0, rw, rl, view);
    if (outline) {
        out.push(strokeRect(outline.x, outline.z, outline.w, outline.l, EColorRef.Ink4, 0.5));
    }
}

function projectLandings(result: IStairCalculationResult, config: IStairConfig, projector: IProjector, out: IPlanPrimitive[]): void {
    const offset: IPlanOffset = stairOffset(result, config);

    for (const landing of result.geometry.landings) {
        const lx: number = projector.pwx(offset.x + landing.position.x);
        const lz: number = projector.pwy(offset.z + landing.position.z);
        const lw: number = projector.pws(landing.width);
        const ld: number = projector.pws(landing.depth);

        out.push(fillRect(lx, lz, lw, ld, EColorRef.Landing));
        out.push(strokeRect(lx, lz, lw, ld, EColorRef.StepStroke, 1));
    }
}

function regularStepPoly(step: IStepGeometry, ox: number, oz: number, projector: IProjector): IPolyPrimitive {
    const x: number = ox + step.position.x;
    const z: number = oz + step.position.z;
    const fill: EColorRef = step.isLanding ? EColorRef.Landing : EColorRef.Step;

    if (step.rotation !== 0) {
        const nose: number = step.noseLength;
        const cx: number = x + step.treadWidth / 2;
        const cz: number = z + (step.treadDepth + nose) / 2 - nose;
        const centerX: number = projector.pwx(cx);
        const centerY: number = projector.pwy(cz);
        const halfW: number = projector.pws(step.treadWidth) / 2;
        const halfH: number = projector.pws(step.treadDepth) / 2;
        const theta: number = (step.rotation * Math.PI) / 180;
        const cos: number = Math.cos(theta);
        const sin: number = Math.sin(theta);
        const corner: (rx: number, ry: number) => IPoint = (rx: number, ry: number): IPoint => [
            centerX + rx * cos + ry * sin,
            centerY - rx * sin + ry * cos,
        ];
        const points: IPoint[] = [corner(-halfW, -halfH), corner(halfW, -halfH), corner(halfW, halfH), corner(-halfW, halfH)];
        return { kind: EPrimitiveKind.Poly, closed: true, stroke: EColorRef.StepStroke, width: 1, points, fill };
    }

    return {
        kind: EPrimitiveKind.Poly,
        points: rectPoints(projector.pwx(x), projector.pwy(z), projector.pws(step.treadWidth), projector.pws(step.treadDepth)),
        closed: true,
        stroke: EColorRef.StepStroke,
        width: 1,
        fill,
    };
}

/** A spiral tread: the four sector corners with the outer and inner edges as true arcs. */
function sectorStepPoly(
    corners: NonNullable<IStepGeometry['winderCorners']>,
    ox: number,
    oz: number,
    projector: IProjector
): IPolyPrimitive {
    const at: (point: { x: number; z: number }) => IPoint = (point: { x: number; z: number }): IPoint => [
        projector.pwx(ox + point.x),
        projector.pwy(oz + point.z),
    ];
    const points: IPoint[] = [at(corners.innerBack), at(corners.outerBack), at(corners.outerFront), at(corners.innerFront)];
    const bulges: readonly number[] = sectorBulges(points[0], points[1], points[2], points[3]);
    return { kind: EPrimitiveKind.Poly, closed: true, fill: EColorRef.Winder, stroke: EColorRef.StepStroke, width: 1, points, bulges };
}

function winderStepPoly(step: IStepGeometry, ox: number, oz: number, projector: IProjector): IPolyPrimitive | null {
    const corners: IStepGeometry['winderCorners'] = step.winderCorners;
    if (!corners) {
        return null;
    }
    const points: IPoint[] = [];
    points.push([projector.pwx(ox + corners.innerBack.x), projector.pwy(oz + corners.innerBack.z)]);
    points.push([projector.pwx(ox + corners.outerBack.x), projector.pwy(oz + corners.outerBack.z)]);
    if (corners.outerMidPoints) {
        for (const point of corners.outerMidPoints) {
            points.push([projector.pwx(ox + point.x), projector.pwy(oz + point.z)]);
        }
    }
    points.push([projector.pwx(ox + corners.outerFront.x), projector.pwy(oz + corners.outerFront.z)]);
    points.push([projector.pwx(ox + corners.innerFront.x), projector.pwy(oz + corners.innerFront.z)]);
    if (corners.innerMidPoints) {
        for (let i: number = corners.innerMidPoints.length - 1; i >= 0; i--) {
            points.push([projector.pwx(ox + corners.innerMidPoints[i].x), projector.pwy(oz + corners.innerMidPoints[i].z)]);
        }
    }
    return { kind: EPrimitiveKind.Poly, closed: true, fill: EColorRef.Winder, stroke: EColorRef.StepStroke, width: 1, points };
}

function stepNumberText(step: IStepGeometry, ox: number, oz: number, projector: IProjector, spiralAt: ISpiralPoint | null): ITextPrimitive {
    let cx: number;
    let cy: number;

    if (spiralAt) {
        cx = projector.pwx(ox + spiralAt.x);
        cy = projector.pwy(oz + spiralAt.z);
    } else if (step.winderCorners) {
        const corners: NonNullable<IStepGeometry['winderCorners']> = step.winderCorners;
        cx = projector.pwx(ox + (corners.innerBack.x + corners.innerFront.x + corners.outerBack.x + corners.outerFront.x) / 4);
        cy = projector.pwy(oz + (corners.innerBack.z + corners.innerFront.z + corners.outerBack.z + corners.outerFront.z) / 4);
    } else if (step.rotation !== 0) {
        const rcx: number = ox + step.position.x + step.treadWidth / 2;
        const rcz: number = oz + step.position.z + (step.treadDepth + step.noseLength) / 2 - step.noseLength;
        cx = projector.pwx(rcx);
        cy = projector.pwy(rcz);
    } else {
        cx = projector.pwx(ox + step.position.x + step.treadWidth / 2);
        cy = projector.pwy(oz + step.position.z + step.treadDepth / 2);
    }

    const fontSize: number = Math.max(9, Math.min(13, projector.pws(step.treadDepth) * 0.5));
    const label: ITextPrimitive = text([cx, cy], String(step.index + 1), ETextAlign.Center, ETextBaseline.Middle, EColorRef.Ink2, {
        weight: 500,
        size: fontSize,
        family: FONT_MONO_FAMILY,
    });
    // A number belongs on its tread: where the treads are too small for it, it is left out.
    return { ...label, optional: true };
}

function stepShape(step: IStepGeometry, config: IStairConfig, offset: IPlanOffset, projector: IProjector): IPolyPrimitive | null {
    if (!step.isWinder || !step.winderCorners) {
        return regularStepPoly(step, offset.x, offset.z, projector);
    }
    // Every spiral tread is an annular sector around the pole; L/U winders are straight-edged.
    return config.type === 'spiral'
        ? sectorStepPoly(step.winderCorners, offset.x, offset.z, projector)
        : winderStepPoly(step, offset.x, offset.z, projector);
}

function projectSteps(result: IStairCalculationResult, config: IStairConfig, projector: IProjector, out: IPlanPrimitive[]): void {
    const offset: IPlanOffset = stairOffset(result, config);

    // A spiral's exit platform is laid in pieces at the upper floor's level: they are no treads
    // and carry no number.
    const platformY: number = result.room.floorHeight + result.room.floorThickness - 0.5;
    const steps: IStepGeometry[] = result.geometry.steps;
    const pole: ISpiralPoint | null = config.type === 'spiral' ? spiralPole(steps) : null;
    for (const step of steps) {
        const shape: IPolyPrimitive | null = stepShape(step, config, offset, projector);
        if (shape) {
            out.push(shape);
        }
        if (config.type !== 'spiral' || step.position.y < platformY) {
            const spiralAt: ISpiralPoint | null = pole && step.winderCorners ? spiralNumberAt(step.winderCorners, steps, pole) : null;
            out.push(stepNumberText(step, offset.x, offset.z, projector, spiralAt));
        }
    }
}

function projectHandrail(railing: IRailingGeometry, ox: number, oz: number, projector: IProjector, out: IPlanPrimitive[]): void {
    const points: IVec3[] = railing.handrail.points;
    if (points.length < 2) {
        return;
    }
    const path: IPoint[] = points.map((point: IVec3): IPoint => [projector.pwx(ox + point.x), projector.pwy(oz + point.z)]);
    out.push({
        kind: EPrimitiveKind.Poly,
        points: path,
        closed: false,
        stroke: EColorRef.Handrail,
        width: Math.max(1.5, projector.pws(railing.handrail.width * 0.5)),
        cap: ELineCap.Round,
        join: ELineJoin.Round,
    });
}

function projectPosts(railing: IRailingGeometry, ox: number, oz: number, projector: IProjector, out: IPlanPrimitive[]): void {
    const pw: number = Math.max(4, projector.pws(railing.postWidth));
    for (const post of railing.posts) {
        const px: number = projector.pwx(ox + post.position.x) - pw / 2;
        const pz: number = projector.pwy(oz + post.position.z) - pw / 2;
        out.push(fillRect(px, pz, pw, pw, EColorRef.Post));
        out.push(line([px + 1, pz + 1], [px + pw - 1, pz + pw - 1], EColorRef.Paper, 1));
        out.push(line([px + pw - 1, pz + 1], [px + 1, pz + pw - 1], EColorRef.Paper, 1));
    }
}

function projectBalusters(railing: IRailingGeometry, ox: number, oz: number, projector: IProjector, out: IPlanPrimitive[]): void {
    const bw: number = Math.max(2, projector.pws(railing.balusterWidth));
    for (const baluster of railing.balusters) {
        const bx: number = projector.pwx(ox + baluster.position.x) - bw / 2;
        const bz: number = projector.pwy(oz + baluster.position.z) - bw / 2;
        out.push(fillRect(bx, bz, bw, bw, EColorRef.Baluster));
    }
}

function projectRailings(result: IStairCalculationResult, config: IStairConfig, projector: IProjector, out: IPlanPrimitive[]): void {
    if (!config.railing.enabled) {
        return;
    }
    const offset: IPlanOffset = stairOffset(result, config);

    for (const railing of result.geometry.railings.map((item: IRailingGeometry) => inStairFrame(item, offset))) {
        projectHandrail(railing, offset.x, offset.z, projector, out);
        projectPosts(railing, offset.x, offset.z, projector, out);
        projectBalusters(railing, offset.x, offset.z, projector, out);
    }
}

function projectOpening(room: IRoomGeometry, projector: IProjector, out: IPlanPrimitive[]): void {
    const ox: number = projector.pwx(room.openingX);
    const oy: number = projector.pwy(room.openingZ);
    const ow: number = projector.pws(room.openingWidth);
    const ol: number = projector.pws(room.openingLength);

    if (room.openingCircular) {
        const cx: number = ox + ow / 2;
        const cy: number = oy + ol / 2;
        const radius: number = ow / 2;
        out.push({
            kind: EPrimitiveKind.Arc,
            center: [cx, cy],
            startAngle: 0,
            endAngle: Math.PI * 2,
            fill: EColorRef.Opening,
            fillAlpha: OPENING_FILL_ALPHA,
            stroke: EColorRef.Opening,
            width: 2,
            dash: OPENING_DASH,
            radius,
        });
    } else {
        out.push(fillRect(ox, oy, ow, ol, EColorRef.Opening, OPENING_FILL_ALPHA));
        out.push({
            kind: EPrimitiveKind.Hatch,
            rect: { x: ox, y: oy, w: ow, h: ol },
            step: HATCH_STEP,
            color: EColorRef.Opening,
            alpha: OPENING_HATCH_ALPHA,
            width: 1,
        });
        out.push(strokeRect(ox, oy, ow, ol, EColorRef.Opening, 2, OPENING_DASH));
    }

    const minDim: number = Math.min(ow, ol);
    if (minDim >= 60) {
        const label: string = $localize`:@@planOpeningLabel:ПРОЁМ`;
        // A round opening is the spiral's whole circle, and its middle is the pole: the label
        // stands outside the circle and its railing, past its upper right.
        const reach: number = (ow / 2) * Math.SQRT1_2 + OPENING_LABEL_GAP;
        out.push(
            room.openingCircular
                ? text(
                      [ox + ow / 2 + reach, oy + ol / 2 - reach],
                      label,
                      ETextAlign.Left,
                      ETextBaseline.Bottom,
                      EColorRef.Opening,
                      OPENING_LABEL_FONT
                  )
                : text([ox + ow / 2, oy + ol / 2], label, ETextAlign.Center, ETextBaseline.Middle, EColorRef.Opening, OPENING_LABEL_FONT)
        );
    }
}

/** An arrowhead at `tip`, pointing along the direction from `from`. */
function arrowHead(from: IPoint, tip: IPoint): IPolyPrimitive {
    const angle: number = Math.atan2(tip[1] - from[1], tip[0] - from[0]);
    return {
        kind: EPrimitiveKind.Poly,
        points: [
            tip,
            [tip[0] - DIRECTION_HEAD_LEN * Math.cos(angle - 0.4), tip[1] - DIRECTION_HEAD_LEN * Math.sin(angle - 0.4)],
            [tip[0] - DIRECTION_HEAD_LEN * Math.cos(angle + 0.4), tip[1] - DIRECTION_HEAD_LEN * Math.sin(angle + 0.4)],
        ],
        closed: true,
        fill: EColorRef.Ink3,
    };
}

/**
 * A spiral's climb is shown by an arc along the walking line over its first treads: a chord from
 * the first to the last tread would be a stub across the start, as the turn closes on itself.
 */
function projectSpiralWalk(steps: readonly IStepGeometry[], offset: IPlanOffset, projector: IProjector, out: IPlanPrimitive[]): void {
    const pole: ISpiralPoint | null = spiralPole(steps);
    const path: IPoint[] = (pole ? spiralWalkArc(steps, pole) : []).map((point: ISpiralPoint): IPoint => [
        projector.pwx(offset.x + point.x),
        projector.pwy(offset.z + point.z),
    ]);
    if (path.length < 2) {
        return;
    }
    out.push({ kind: EPrimitiveKind.Poly, points: path, closed: false, stroke: EColorRef.Ink4, width: 1.5 });
    out.push(arrowHead(path[path.length - 2], path[path.length - 1]));
}

function projectDirectionArrow(result: IStairCalculationResult, config: IStairConfig, projector: IProjector, out: IPlanPrimitive[]): void {
    const steps: IStepGeometry[] = result.geometry.steps;
    if (steps.length < 2) {
        return;
    }

    const offset: IPlanOffset = stairOffset(result, config);
    if (config.type === 'spiral') {
        projectSpiralWalk(steps, offset, projector, out);
        return;
    }
    // The walking line through every tread, the arrow on the top one; the label stands before its start.
    projectWalkingLine(steps, offset, projector, out);
    const [first, second]: IStairPoint[] = walkingLine(steps);
    const back: number = Math.max(1, Math.hypot(first.x - second.x, first.z - second.z));
    const reach: number = result.treadDepth / back;
    out.push(
        text(
            [
                projector.pwx(offset.x + first.x + (first.x - second.x) * reach),
                projector.pwy(offset.z + first.z + (first.z - second.z) * reach),
            ],
            $localize`:@@homeTypeArrowUp:↑ ВВЕРХ`,
            ETextAlign.Center,
            ETextBaseline.Middle,
            EColorRef.Ink3,
            ARROW_LABEL_FONT
        )
    );
}

function projectDimensions(
    result: IStairCalculationResult,
    config: IStairConfig,
    projector: IProjector,
    out: IPlanPrimitive[],
    measurer: ITextMeasurer
): void {
    const room: IRoomGeometry = result.room;

    const oxL: number = projector.pwx(room.openingX);
    const oxR: number = projector.pwx(room.openingX + room.openingWidth);
    const ozT: number = projector.pwy(room.openingZ);
    const ozB: number = projector.pwy(room.openingZ + room.openingLength);

    const rightX: number = projector.pwx(room.roomWidth) + 28;
    const bottomYOpening: number = projector.pwy(room.roomLength) + 18;
    const bottomYStair: number = projector.pwy(room.roomLength) + 36;

    // Opening length (right of room) with extension lines from the opening's top/bottom edges.
    out.push(line([oxR, ozT], [rightX, ozT], EColorRef.Opening, EXT_LINE_WIDTH, { dash: EXT_LINE_DASH, strokeAlpha: EXT_LINE_ALPHA }));
    out.push(line([oxR, ozB], [rightX, ozB], EColorRef.Opening, EXT_LINE_WIDTH, { dash: EXT_LINE_DASH, strokeAlpha: EXT_LINE_ALPHA }));
    out.push(verticalDim(rightX, ozT, ozB, String(room.openingLength), measurer, EColorRef.Opening));

    // Opening width (below the bottom wall).
    out.push(
        line([oxL, ozB], [oxL, bottomYOpening], EColorRef.Opening, EXT_LINE_WIDTH, { dash: EXT_LINE_DASH, strokeAlpha: EXT_LINE_ALPHA })
    );
    out.push(
        line([oxR, ozB], [oxR, bottomYOpening], EColorRef.Opening, EXT_LINE_WIDTH, { dash: EXT_LINE_DASH, strokeAlpha: EXT_LINE_ALPHA })
    );
    out.push(horizontalDim(oxL, bottomYOpening, oxR, String(room.openingWidth), measurer, EColorRef.Opening));

    // Stair width (extension lines from the first step's left/right edges down to the dim row).
    const offset: IPlanOffset = stairOffset(result, config);
    const firstStep: IStepGeometry | undefined = result.geometry.steps.at(0);
    if (firstStep && !firstStep.isWinder) {
        const swX1: number = projector.pwx(offset.x + firstStep.position.x);
        const swX2: number = projector.pwx(offset.x + firstStep.position.x + firstStep.treadWidth);
        const stepBackY: number = projector.pwy(offset.z + firstStep.position.z + firstStep.treadDepth);

        out.push(
            line([swX1, stepBackY], [swX1, bottomYStair], EColorRef.StairWidth, EXT_LINE_WIDTH, {
                dash: EXT_LINE_DASH,
                strokeAlpha: EXT_LINE_ALPHA,
            })
        );
        out.push(
            line([swX2, stepBackY], [swX2, bottomYStair], EColorRef.StairWidth, EXT_LINE_WIDTH, {
                dash: EXT_LINE_DASH,
                strokeAlpha: EXT_LINE_ALPHA,
            })
        );
        out.push(horizontalDim(swX1, bottomYStair, swX2, String(config.stairWidth), measurer, EColorRef.StairWidth));
    }
}

function projectAxes(room: IRoomGeometry, projector: IProjector, out: IPlanPrimitive[]): void {
    const originX: number = projector.pwx(0);
    const originY: number = projector.pwy(room.roomLength);
    const ox: number = originX + AXIS_INSET;
    const oy: number = originY - AXIS_INSET;

    // X arrow
    out.push(line([ox, oy], [ox + AXIS_ARROW_LEN, oy], EColorRef.Axis, 2));
    out.push({
        kind: EPrimitiveKind.Poly,
        points: [
            [ox + AXIS_ARROW_LEN, oy],
            [ox + AXIS_ARROW_LEN - AXIS_HEAD_LEN, oy - AXIS_HEAD_LEN / 2],
            [ox + AXIS_ARROW_LEN - AXIS_HEAD_LEN, oy + AXIS_HEAD_LEN / 2],
        ],
        closed: true,
        fill: EColorRef.Axis,
    });
    out.push(text([ox + AXIS_ARROW_LEN + 5, oy], 'X', ETextAlign.Left, ETextBaseline.Middle, EColorRef.Axis, AXIS_LABEL_FONT));

    // Y arrow
    out.push(line([ox, oy], [ox, oy - AXIS_ARROW_LEN], EColorRef.Axis, 2));
    out.push({
        kind: EPrimitiveKind.Poly,
        points: [
            [ox, oy - AXIS_ARROW_LEN],
            [ox - AXIS_HEAD_LEN / 2, oy - AXIS_ARROW_LEN + AXIS_HEAD_LEN],
            [ox + AXIS_HEAD_LEN / 2, oy - AXIS_ARROW_LEN + AXIS_HEAD_LEN],
        ],
        closed: true,
        fill: EColorRef.Axis,
    });
    out.push(text([ox, oy - AXIS_ARROW_LEN - 10], 'Y', ETextAlign.Center, ETextBaseline.Middle, EColorRef.Axis, AXIS_LABEL_FONT));

    out.push(text([ox - 8, oy + 14], '0', ETextAlign.Right, ETextBaseline.Middle, EColorRef.Axis, AXIS_ORIGIN_FONT));
}

/**
 * Top view (вид сверху) — room walls, opening, treads/winders with numbers,
 * railings, the up-direction arrow, plan dimensions and the X/Y axis gizmo.
 * Pure projection in pane-local pixels; the drawer translates to the pane origin.
 * 1:1 port of the former PlanViewComponent#drawTopPlan and its helpers. With `fit` the sheet sets
 * the scale and the pane point of the room's (0, 0) corner.
 */
export function projectTopPlan(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer,
    fit?: IViewFit
): IProjectionOutput {
    const room: IRoomGeometry = result.room;
    const focus: IPlanBox = focusBBox(result, config);

    const innerW: number = pane.w - PAD_LEFT - PAD_RIGHT;
    const innerH: number = pane.h - PAD_TOP - PAD_BOTTOM;
    const fitScale: number = Math.min(innerW / focus.w, innerH / focus.l);
    const scale: number = fit?.pxPerMm ?? Math.min(PLAN_FIXED_PX_PER_MM, fitScale);

    // Pane-local: pin the focus's left edge to the pane left, bottom edge to the pane bottom.
    const offsetX: number = fit ? fit.origin.x : PAD_LEFT - focus.x * scale;
    const offsetY: number = fit ? fit.origin.y : pane.h - PAD_BOTTOM - (focus.z + focus.l) * scale;

    const projector: IProjector = {
        pwx: (x: number): number => offsetX + x * scale,
        pwy: (z: number): number => offsetY + z * scale,
        pws: (s: number): number => s * scale,
    };

    const primitives: IPlanPrimitive[] = [];
    projectRoom(room, focus, projector, primitives);
    projectLandings(result, config, projector, primitives);
    projectSteps(result, config, projector, primitives);
    projectRailings(result, config, projector, primitives);
    projectOpening(room, projector, primitives);
    projectDirectionArrow(result, config, projector, primitives);
    projectDimensions(result, config, projector, primitives, measurer);
    if (config.type !== 'spiral') {
        const offset: IPlanOffset = stairOffset(result, config);
        projectTreadMarks(result, config.railing.enabled, offset, projector, primitives);
        projectFlightLengths(result.geometry.steps, offset, projector, measurer, primitives);
    }
    projectParameterNotes(result, projector, primitives);
    projectAxes(room, projector, primitives);

    // Millimetre origin: the room's (x = 0, z = 0) corner; plan Z grows down the screen.
    return { primitives, pxPerMm: scale, origin: { x: offsetX, y: offsetY } };
}
